import { AlertTriangle, Camera, Mic, Square } from 'lucide-react';
import { useState } from 'react';
import { hlaskaZChyby } from '../../api/client.js';
import type { Objekt } from '../../api/types.js';
import { Banner, Chyba, Textarea, VelkeTlacitko } from '../../components/ui.js';
import { useDiktovani } from '../../lib/diktovani.js';
import { odesliNeboZarad, type FrontaSoubor } from '../../lib/offline.js';
import { dnesniDen } from './datum.js';
import { sledujUdalost } from '../../lib/analytika.js';

/**
 * NÁLEZ (wireframe 13 §1, tok 3 kroků, pod minutu): foto → diktát/text →
 * objekt → ODESLAT → potvrzení. Mistr NEKVALIFIKUJE — jen hlásí. Vyžaduje
 * aspoň jedno z foto/popis (jinak kancelář nemá co posoudit, A2.3). POST
 * /api/diary/nalez založí Nalez (zdroj teren); vazbu na deníkový záznam řeší
 * SERVER podle dne založení. Id záznamu se NEPOSÍLÁ. Fotky se věší na Nalez.
 * Funguje i na podepsaném dni. Offline jde celý nález do fronty (den se pamatuje).
 */
export function NalezWizard({
  zakazkaId,
  objekty,
  zavri,
}: {
  zakazkaId: string;
  objekty: Objekt[];
  zavri: (hotovo: boolean) => void;
}) {
  const [krok, setKrok] = useState<1 | 2 | 3>(1);
  const [fotky, setFotky] = useState<File[]>([]);
  const [popis, setPopis] = useState('');
  // Default: má-li zakázka jediný objekt, předvyber ho (blueprint 16 §13 — defaulty
  // místo rozhodování; mistr může odznačit).
  const [objektId, setObjektId] = useState<string | null>(
    objekty.length === 1 ? (objekty[0]?.id ?? null) : null,
  );
  const [odesilam, setOdesilam] = useState(false);
  const [chyba, setChyba] = useState<string | null>(null);
  // Po odeslání (A2.1 / TOP 7a) — jasné potvrzení: online × offline fronta.
  const [odeslano, setOdeslano] = useState<'online' | 'offline' | null>(null);

  const diktovani = useDiktovani((usek) => setPopis((p) => (p ? `${p.trimEnd()} ${usek}` : usek)));

  const maFoto = fotky.length > 0;
  const maPopis = popis.trim().length > 0;

  const odeslat = async () => {
    if (!maFoto && !maPopis) {
      setChyba('Přidejte fotku nebo pár slov — jinak kancelář nemá co posoudit.');
      return;
    }
    setOdesilam(true);
    setChyba(null);
    try {
      const soubory: FrontaSoubor[] = fotky.map((f) => ({
        blob: f,
        nazevSouboru: f.name || 'nalez.jpg',
        mimeType: f.type || 'image/jpeg',
        popis: popis || undefined,
      }));
      const hned = await odesliNeboZarad({
        druh: 'nalez',
        zakazkaId,
        telo: {
          datum: dnesniDen(),
          ...(popis.trim() ? { popis: popis.trim() } : {}),
          ...(objektId ? { objektId } : {}),
        },
        soubory,
        popis: 'Nález z terénu',
      });
      sledujUdalost('nalez_zalozen', { rezim: hned ? 'online' : 'offline' });
      setOdeslano(hned ? 'online' : 'offline');
    } catch (err) {
      setChyba(hlaskaZChyby(err));
      setOdesilam(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-overlay p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={odeslano ? 'Nález odeslán' : `Nález — krok ${krok} ze 3`}
        className="mx-auto flex w-full max-w-md flex-col gap-4 rounded-2xl bg-surface p-5 shadow-xl"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">
            {odeslano ? (
              '✓ Nález odeslán'
            ) : (
              <span className="flex items-center gap-2">
                <AlertTriangle size={20} strokeWidth={2} aria-hidden="true" /> NÁLEZ {krok}/3
              </span>
            )}
          </h2>
          <button
            type="button"
            className="min-h-12 min-w-12 text-2xl"
            aria-label="Zavřít"
            onClick={() => zavri(!!odeslano)}
          >
            ×
          </button>
        </div>

        {chyba ? <Chyba>{chyba}</Chyba> : null}

        {odeslano ? (
          <>
            <Banner ton={odeslano === 'online' ? 'success' : 'info'}>
              {odeslano === 'online'
                ? 'Nález odeslán — přípravář ho uvidí ve frontě. Uvidíte ho v Menu → Moje nálezy; kancelář rozhodne, jestli je to vícepráce.'
                : 'Uloženo — nález se odešle po připojení (je ve frontě). Uvidíte ho v Menu → Moje nálezy.'}
            </Banner>
            <VelkeTlacitko onClick={() => zavri(true)}>Hotovo</VelkeTlacitko>
          </>
        ) : null}

        {!odeslano && krok === 1 && (
          <>
            <p className="text-base text-text-secondary">Vyfoťte to</p>
            <label className="flex min-h-20 cursor-pointer flex-col items-center justify-center rounded-xl bg-primary px-4 text-lg font-bold text-white hover:bg-primary-hover">
              <span className="flex items-center gap-2">
                <Camera size={20} strokeWidth={2} aria-hidden="true" /> VYFOTIT
              </span>
              <span className="text-sm font-normal">(možno víc fotek)</span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="sr-only"
                aria-label="Vyfotit nález"
                onChange={(e) => {
                  if (e.target.files) setFotky((f) => [...f, ...Array.from(e.target.files!)]);
                  e.target.value = '';
                }}
              />
            </label>
            {maFoto && (
              <p className="text-base">Přidáno {fotky.length} foto — lze vyfotit další.</p>
            )}
            {/* Jedna primární akce: bez fota je cesta vedlejší (secondary). */}
            <VelkeTlacitko varianta={maFoto ? 'primary' : 'secondary'} onClick={() => setKrok(2)}>
              {maFoto ? 'Dál' : 'Pokračovat bez fota'}
            </VelkeTlacitko>
          </>
        )}

        {!odeslano && krok === 2 && (
          <>
            <p className="text-base text-text-secondary">Řekněte, co nesedí</p>
            {diktovani.podporovano && (
              <VelkeTlacitko
                varianta={diktovani.diktuji ? 'podpis' : 'primary'}
                onClick={diktovani.prepni}
              >
                {diktovani.diktuji ? (
                  <>
                    <Square size={16} fill="currentColor" aria-hidden="true" /> ZASTAVIT DIKTÁT
                  </>
                ) : (
                  <>
                    <Mic size={18} strokeWidth={2} aria-hidden="true" /> NADIKTOVAT
                  </>
                )}
              </VelkeTlacitko>
            )}
            <Textarea
              aria-label="Popis nálezu"
              rows={4}
              value={popis}
              onChange={(e) => setPopis(e.target.value)}
              placeholder={'Co nesedí… (např. „pod podlahou je jiná zemina, než je v projektu")'}
            />
            {diktovani.prubezny ? (
              <p aria-live="polite" className="text-base text-text-secondary italic">
                {diktovani.prubezny}…
              </p>
            ) : null}
            <VelkeTlacitko varianta={maPopis ? 'primary' : 'secondary'} onClick={() => setKrok(3)}>
              {maPopis ? 'Dál' : 'Pokračovat bez popisu'}
            </VelkeTlacitko>
          </>
        )}

        {!odeslano && krok === 3 && (
          <>
            <p className="text-lg font-semibold">Kde to je?</p>
            {objekty.length === 0 && (
              <p className="text-base text-text-secondary">
                Zakázka nemá objekty — nález se odešle bez místa.
              </p>
            )}
            <div className="flex flex-col gap-2">
              {objekty.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  aria-pressed={objektId === o.id}
                  className={`min-h-14 rounded-xl border-2 px-4 text-left text-lg font-semibold ${
                    objektId === o.id
                      ? 'border-primary bg-primary text-white'
                      : 'border-border-strong bg-surface hover:bg-bg'
                  }`}
                  onClick={() => setObjektId(objektId === o.id ? null : o.id)}
                >
                  {o.kod} — {o.nazev}
                </button>
              ))}
            </div>
            <VelkeTlacitko onClick={() => void odeslat()} disabled={odesilam}>
              {odesilam ? 'Odesílám…' : 'ODESLAT'}
            </VelkeTlacitko>
            <p className="text-sm text-text-secondary">
              Nález padá do fronty přípraváře — jestli je to vícepráce, rozhodne kancelář.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
